'use client';
import React from 'react';
import Link from 'next/link';

interface MethodCardProps {
    icon: React.ReactNode;
    iconColor: string;
    title: string;
    description: string;
    href?: string;
    category: string;
    comingSoon?: boolean;
}

const tagVariants: Record<string, string> = {
    method: 'bg-purple-soft text-purple-ink',
    visualiser: 'bg-teal-soft text-teal-ink',
    reference: 'bg-amber-soft text-amber-ink',
};

const tagBase =
    'inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-medium uppercase tracking-[0.02em] capitalize';

export default function MethodCard({
    icon,
    iconColor,
    title,
    description,
    href,
    category,
    comingSoon = false,
}: MethodCardProps) {
    const tagClass = tagVariants[category] ?? 'bg-subtle text-hint';

    const content = (
        <div
            className={`relative flex h-full flex-col gap-3 rounded-lg border-[0.5px] border-line bg-white p-5 transition-colors ${
                comingSoon
                    ? 'cursor-not-allowed opacity-[0.55]'
                    : 'cursor-pointer hover:border-line-strong'
            }`}
        >
            <div
                className="flex h-[34px] w-[34px] items-center justify-center rounded-md text-lg"
                style={{
                    background: `var(--color-${iconColor}-soft)`,
                    color: `var(--color-${iconColor})`,
                }}
            >
                {icon}
            </div>

            <div className="flex-1">
                <h3 className="mb-1 text-[15px] font-medium">{title}</h3>
                <p className="text-[13px] leading-snug text-muted">{description}</p>
            </div>

            <div className="mt-2 flex items-center justify-between">
                {comingSoon ? (
                    <span className={`${tagBase} bg-subtle text-hint`}>Soon</span>
                ) : (
                    <>
                        <span className={`${tagBase} ${tagClass}`}>{category}</span>
                        <span className="text-base text-hint">→</span>
                    </>
                )}
            </div>
        </div>
    );

    if (comingSoon || !href) {
        return content;
    }

    return (
        <Link href={href} className="block h-full">
            {content}
        </Link>
    );
}
